import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, User, Mail, Phone, Building2, Key } from 'lucide-react'
import { ApiService } from '@/services/api'
import type { User as UserType, Restaurant } from '@/types'

interface EditUserFormProps {
  user: UserType | null
  open: boolean
  onClose: () => void
  onSuccess: (user: UserType) => void
}

interface FormData {
  email: string
  name: string
  phone: string
  userType: 'admin' | 'restaurant_owner'
  restaurantIds: string[]
  isActive: boolean
  resetPassword?: boolean
  newPassword?: string
}

export default function EditUserForm({ user, open, onClose, onSuccess }: EditUserFormProps) {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    name: '',
    phone: '',
    userType: 'restaurant_owner',
    restaurantIds: [],
    isActive: true,
    resetPassword: false,
    newPassword: ''
  })
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && user) {
      const existingIds = user.restaurants && user.restaurants.length > 0
        ? user.restaurants.map(r => r.restaurantId)
        : (user.restaurantId ? [user.restaurantId] : [])
      setFormData({
        email: user.email,
        name: user.name,
        phone: user.phone,
        userType: user.userType,
        restaurantIds: existingIds,
        isActive: user.isActive,
        resetPassword: false,
        newPassword: ''
      })
      loadRestaurants()
    }
  }, [open, user])

  const loadRestaurants = async () => {
    setIsLoadingRestaurants(true)
    try {
      const response = await ApiService.getAllRestaurants()
      if (response.success) {
        setRestaurants(response.data)
      }
    } catch (err) {
      console.error('Failed to load restaurants:', err)
    } finally {
      setIsLoadingRestaurants(false)
    }
  }

  const generatePassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < 12; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length))
    }
    return password
  }

  const handleGeneratePassword = () => {
    const newPassword = generatePassword()
    setFormData(prev => ({ 
      ...prev, 
      newPassword,
      resetPassword: true 
    }))
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const validateForm = () => {
    if (!formData.email || !formData.name || !formData.phone) {
      return 'Please fill in all required fields'
    }

    if (formData.userType === 'restaurant_owner' && formData.restaurantIds.length === 0) {
      return 'Please select at least one restaurant for restaurant owner'
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address'
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // First, update user details
      const updateData = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        userType: formData.userType,
        restaurantIds: formData.userType === 'restaurant_owner' ? formData.restaurantIds : [],
        isActive: formData.isActive
      }

      const response = await ApiService.updateUser(user!._id, updateData)

      if (!response.success) {
        setError(response.message || 'Failed to update user')
        return
      }

      // If password reset is requested, use the admin reset credentials endpoint
      if (formData.resetPassword && formData.newPassword) {
        const resetResponse = await ApiService.resetUserCredentials(user!._id, {
          password: formData.newPassword
        })

        if (!resetResponse.success) {
          setError('User updated but password reset failed: ' + (resetResponse.message || 'Unknown error'))
          return
        }
      }

      onSuccess(response.data)
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setError(null)
    setFormData({
      email: '',
      name: '',
      phone: '',
      userType: 'restaurant_owner',
      restaurantIds: [],
      isActive: true,
      resetPassword: false,
      newPassword: ''
    })
    onClose()
  }

  if (!user) return null

  const toggleRestaurant = (restaurantId: string) => {
    setFormData(prev => ({
      ...prev,
      restaurantIds: prev.restaurantIds.includes(restaurantId)
        ? prev.restaurantIds.filter(id => id !== restaurantId)
        : [...prev.restaurantIds, restaurantId]
    }))
  }

  const selectedRestaurants = restaurants.filter(r => formData.restaurantIds.includes(r._id))

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Edit User</span>
          </DialogTitle>
          <DialogDescription>
            Update user account information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* User Type */}
          <div className="space-y-2">
            <Label htmlFor="userType">User Type *</Label>
            <Select
              value={formData.userType}
              onValueChange={(value) => handleInputChange('userType', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin User</SelectItem>
                <SelectItem value="restaurant_owner">Restaurant Owner</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+919876543210"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>

          {/* Restaurant Selection for Restaurant Owners — select multiple to
              make this a "Head of Restaurant" account (owns more than one restaurant) */}
          {formData.userType === 'restaurant_owner' && (
            <div className="space-y-2">
              <Label>Restaurants * {formData.restaurantIds.length > 1 && (
                <span className="text-xs font-normal text-muted-foreground">(Head of Restaurant — {formData.restaurantIds.length} selected)</span>
              )}</Label>
              <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                {isLoadingRestaurants ? (
                  <div className="p-3 text-sm text-muted-foreground">Loading restaurants...</div>
                ) : (
                  restaurants.map((restaurant) => (
                    <label
                      key={restaurant._id}
                      className="flex items-center space-x-2 p-2.5 cursor-pointer hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={formData.restaurantIds.includes(restaurant._id)}
                        onChange={() => toggleRestaurant(restaurant._id)}
                        className="rounded"
                      />
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm">{restaurant.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedRestaurants.length > 0 && (
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {selectedRestaurants.map(r => (
                    <p key={r._id}>{r.name} — Owner: {r.ownerName} | Database: {r.databaseName}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="isActive">Account Status</Label>
            <Select
              value={formData.isActive.toString()}
              onValueChange={(value) => handleInputChange('isActive', value === 'true')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Password Reset Section */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="resetPassword"
                  checked={formData.resetPassword}
                  onChange={(e) => handleInputChange('resetPassword', e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="resetPassword" className="text-sm font-medium">
                  Reset user password
                </Label>
              </div>
              <Key className="h-4 w-4 text-muted-foreground" />
            </div>

            {formData.resetPassword && (
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGeneratePassword}
                    className="flex items-center space-x-2"
                  >
                    <Key className="h-4 w-4" />
                    <span>Generate Secure Password</span>
                  </Button>
                  <span className="text-xs text-muted-foreground">or enter manually below</span>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password (min. 6 characters)</Label>
                  <Input
                    id="newPassword"
                    type="text"
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    placeholder="Enter new password"
                    className="font-mono"
                  />
                </div>

                {formData.newPassword && formData.newPassword.length >= 6 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">New Password Set:</p>
                    <p className="text-lg font-mono bg-white px-2 py-1 rounded border text-gray-900 mt-1 break-all">
                      {formData.newPassword}
                    </p>
                    <p className="text-xs text-blue-700 mt-2">
                      <strong>Important:</strong> Copy and save this password securely. The user will need this to login.
                    </p>
                  </div>
                )}

                {formData.newPassword && formData.newPassword.length < 6 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Password must be at least 6 characters long
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || isLoadingRestaurants}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Updating User...' : 'Update User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}